import type { Order } from '../types'

/**
 * Rapor sayfasının grafikleri için günün tamamlanmış siparişlerinden çıkan
 * özetler. Saf hesap: ekran, kasa ve test aynı sonucu görüyor.
 *
 * Ciro satırlara dağıtılırken indirim de dağıtılıyor: satırların toplamı
 * siparişin ödenen tutarına oranlanıyor. Böylece kişilerin, saatlerin ve
 * kategorilerin toplamı "Toplam Ciro" kartıyla aynı rakamı veriyor.
 */

export interface PersonStat {
  name: string
  kurus: number
  /** Bu kişinin en az bir satır girdiği sipariş sayısı. */
  orders: number
  items: number
}

export interface HourStat {
  hour: number
  kurus: number
  orders: number
}

export interface ShareStat {
  name: string
  kurus: number
}

export interface ProductStat {
  name: string
  qty: number
  kurus: number
}

/** Satırı kimin girdiği bilinmiyorsa (eski kayıtlar). */
export const UNKNOWN_PERSON = 'Belirtilmemiş'

/** Siparişin satırlarına ödenen tutarı oranlayan çarpan. */
function payRatio(o: Order): number {
  const lines = o.lines.reduce((s, l) => s + l.lineTotalKurus, 0)
  return lines > 0 ? o.totalKurus / lines : 0
}

export function byPerson(orders: Order[]): PersonStat[] {
  const map = new Map<string, PersonStat & { seen: Set<string> }>()
  for (const o of orders) {
    const r = payRatio(o)
    for (const l of o.lines) {
      const name = l.byName || o.openedByName || UNKNOWN_PERSON
      const cur = map.get(name) ?? { name, kurus: 0, orders: 0, items: 0, seen: new Set<string>() }
      cur.kurus += l.lineTotalKurus * r
      cur.items += l.qty
      if (!cur.seen.has(o.id)) {
        cur.seen.add(o.id)
        cur.orders += 1
      }
      map.set(name, cur)
    }
  }
  return [...map.values()]
    .map(({ seen: _seen, ...p }) => ({ ...p, kurus: Math.round(p.kurus) }))
    .sort((a, b) => b.kurus - a.kurus)
}

/** Saat saat; satışın olduğu ilk ve son saat arası, boş saatler sıfır. */
export function byHour(orders: Order[]): HourStat[] {
  if (orders.length === 0) return []
  const map = new Map<number, HourStat>()
  for (const o of orders) {
    const hour = new Date(o.createdAtLocal).getHours()
    const cur = map.get(hour) ?? { hour, kurus: 0, orders: 0 }
    cur.kurus += o.totalKurus
    cur.orders += 1
    map.set(hour, cur)
  }
  const hours = [...map.keys()]
  let from = Math.min(...hours)
  let to = Math.max(...hours)
  // En az altı sütun: tek saatlik bir gün tek, kalın bir çubuk olmasın.
  while (to - from < 5) {
    if (from > 0) from -= 1
    if (to - from < 5 && to < 23) to += 1
    if (from === 0 && to === 23) break
  }
  const out: HourStat[] = []
  for (let h = from; h <= to; h++) out.push(map.get(h) ?? { hour: h, kurus: 0, orders: 0 })
  return out
}

/**
 * Kategoriye göre ciro. İlk `top` kategori kendi adıyla, kalanı "Diğer":
 * renkler sabit sırada ve sınırlı, dokuzuncu bir renk üretilmiyor.
 */
export function byCategory(
  orders: Order[],
  categoryOf: (productId: string) => string | undefined,
  top = 5,
): ShareStat[] {
  const map = new Map<string, number>()
  for (const o of orders) {
    const r = payRatio(o)
    for (const l of o.lines) {
      const name = categoryOf(l.productId) ?? 'Diğer'
      map.set(name, (map.get(name) ?? 0) + l.lineTotalKurus * r)
    }
  }
  const all = [...map.entries()]
    .map(([name, kurus]) => ({ name, kurus: Math.round(kurus) }))
    .filter((c) => c.kurus > 0)
    .sort((a, b) => b.kurus - a.kurus)
  if (all.length <= top + 1) return all
  const head = all.slice(0, top).filter((c) => c.name !== 'Diğer')
  const rest = all.filter((c) => !head.includes(c)).reduce((s, c) => s + c.kurus, 0)
  return [...head, { name: 'Diğer', kurus: rest }]
}

export function byProduct(orders: Order[]): ProductStat[] {
  const map = new Map<string, ProductStat>()
  for (const o of orders) {
    const r = payRatio(o)
    for (const l of o.lines) {
      const cur = map.get(l.productId) ?? { name: l.name, qty: 0, kurus: 0 }
      cur.qty += l.qty
      cur.kurus += l.lineTotalKurus * r
      map.set(l.productId, cur)
    }
  }
  return [...map.values()]
    .map((p) => ({ ...p, kurus: Math.round(p.kurus) }))
    .sort((a, b) => b.qty - a.qty || b.kurus - a.kurus)
}

/* ---------- dönem raporu (11 Eki 2026) ---------- */

export interface DayStat {
  /** 'YYYY-MM-DD' */
  day: string
  kurus: number
  orders: number
}

/** 'YYYY-MM-DD' günlerini sırayla, iki uç dahil. */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = []
  const d = new Date(`${from}T12:00:00`)
  const end = new Date(`${to}T12:00:00`)
  while (d <= end) {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    out.push(`${y}-${m}-${dd}`)
    d.setDate(d.getDate() + 1)
  }
  return out
}

/** Gün gün ciro; satış olmayan gün sıfırla, çizgi kopmasın. */
export function byDay(orders: Order[], from: string, to: string): DayStat[] {
  const map = new Map<string, DayStat>(daysBetween(from, to).map((day) => [day, { day, kurus: 0, orders: 0 }]))
  for (const o of orders) {
    const cur = map.get(o.dayKey)
    if (!cur) continue
    cur.kurus += o.totalKurus
    cur.orders += 1
  }
  return [...map.values()]
}

/**
 * Haftanın günü × saat yoğunluğu. Satır 0 = Pazartesi. Değer, o gün-saatte
 * dönem boyunca alınan toplam ciro ve sipariş.
 */
export function byWeekdayHour(orders: Order[]): { kurus: number; orders: number }[][] {
  const grid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => ({ kurus: 0, orders: 0 })))
  for (const o of orders) {
    const d = new Date(o.createdAtLocal)
    const row = (d.getDay() + 6) % 7
    const cell = grid[row][d.getHours()]
    cell.kurus += o.totalKurus
    cell.orders += 1
  }
  return grid
}

export interface PaymentStat {
  key: 'cash' | 'card' | 'meal' | 'credit' | 'other'
  label: string
  kurus: number
  count: number
}

/**
 * Ödeme yöntemine göre. Kısmi ödeme nakit ve kart paylarına bölünüyor;
 * eski kayıtlardaki "Ödendi" ve "İndirimli" (yöntemsiz) "Diğer"de.
 */
export function byPayment(orders: Order[]): PaymentStat[] {
  const acc: Record<PaymentStat['key'], PaymentStat> = {
    cash: { key: 'cash', label: 'Nakit', kurus: 0, count: 0 },
    card: { key: 'card', label: 'Kart', kurus: 0, count: 0 },
    meal: { key: 'meal', label: 'Yemek kartı', kurus: 0, count: 0 },
    credit: { key: 'credit', label: 'Cari (borç)', kurus: 0, count: 0 },
    other: { key: 'other', label: 'Diğer', kurus: 0, count: 0 },
  }
  for (const o of orders) {
    if (o.paymentMethod === 'split' && o.split) {
      acc.cash.kurus += o.split.cashKurus
      acc.card.kurus += o.split.cardKurus
      if (o.split.cashKurus > 0) acc.cash.count += 1
      if (o.split.cardKurus > 0) acc.card.count += 1
      continue
    }
    const k: PaymentStat['key'] =
      o.paymentMethod === 'cash' ||
      o.paymentMethod === 'card' ||
      o.paymentMethod === 'meal' ||
      o.paymentMethod === 'credit'
        ? o.paymentMethod
        : 'other'
    acc[k].kurus += o.totalKurus
    acc[k].count += 1
  }
  return Object.values(acc).filter((p) => p.kurus > 0)
}

/** Önceki döneme göre değişim yüzdesi; önceki sıfırsa null. */
export function change(now: number, before: number): number | null {
  if (before <= 0) return null
  return ((now - before) / before) * 100
}
